import React, { useState, useEffect, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Card, Badge, ListGroup, Modal, Button, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { autoAnimate } from "@formkit/auto-animate";
import {
  faStar,
  faTicketAlt,
  faClock,
  faComments,
  faSmile,
  faFrown,
  faMeh,
  faChevronLeft,
  faChevronRight,
  faEye,
  faEyeSlash,
  faClipboardList,
  faChartBar,
  faEdit,
  faDownload,
  faFilePdf,
  faFilter,
  faSearch,
  faCalendarAlt,
  faTimes,
  faCheck,
  faChevronDown,
  faTag,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import "./Feed.css";

// Import survey icons (same as MySurvey.js)
import angry from "../../../assets/img/survey-icons/angry-face.svg";
import angryBlack from "../../../assets/img/survey-icons/angry-face-black.svg";
import frownSvg from "../../../assets/img/survey-icons/frown.svg";
import frownBlack from "../../../assets/img/survey-icons/frown-black.svg";
import happyHeart from "../../../assets/img/survey-icons/happy-heart.svg";
import happyHeartBlack from "../../../assets/img/survey-icons/happy-heart-black.svg";
import mehSvg from "../../../assets/img/survey-icons/meh.svg";
import mehBlack from "../../../assets/img/survey-icons/meh-black.svg";
import smileSvg from "../../../assets/img/survey-icons/smile.svg";
import smileBlack from "../../../assets/img/survey-icons/smile-black.svg";
import starSvg from "../../../assets/img/survey-icons/star.svg";
import starBlack from "../../../assets/img/survey-icons/star-black.svg";
import thumbDownSvg from "../../../assets/img/survey-icons/thumb-down.svg";
import thumbDownBlack from "../../../assets/img/survey-icons/thumb-down-black.svg";
import thumbUpBlack from "../../../assets/img/survey-icons/thumbs-up-black.svg";
import thumbUpSvg from "../../../assets/img/survey-icons/thumbs-up.svg";

// Create arrays like in MySurvey.js
const smileys = [angry, frownSvg, mehSvg, smileSvg, happyHeart];
const smileysBlack = [
  angryBlack,
  frownBlack,
  mehBlack,
  smileBlack,
  happyHeartBlack,
];

// Animated Counter Component
const AnimatedCounter = ({ value, decimals = 0, duration = 2000, suffix = "" }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);
    const startValue = 0;
    const endValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    const startTime = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOut;
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <span className={isAnimating ? "counter-animating" : ""}>
      {decimals > 0 ? displayValue.toFixed(decimals) : Math.floor(displayValue)}{suffix}
    </span>
  );
};

// Read More Text Component
const ReadMoreText = ({ text, maxLength = 120 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text && text.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? text : `${text.substring(0, maxLength)}...`;

  if (!text) {
    return <p className="mb-2 text-muted" style={{ fontStyle: "italic" }}>No comment provided</p>;
  }

  return (
    <p className="mb-2" style={{ color: "#495057", lineHeight: "1.6" }}>
      {displayText}
      {shouldTruncate && (
        <button
          className="read-more-btn"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? " Read less" : " Read more"}
        </button>
      )}
    </p>
  );
};

// Skeleton Loader Component
const FeedbackSkeleton = () => {
  return (
    <div className="feedback-skeleton">
      <div className="skeleton-card">
        <div className="skeleton-header">
          <div className="skeleton-avatar"></div>
          <div className="skeleton-content">
            <div className="skeleton-line skeleton-title"></div>
            <div className="skeleton-line skeleton-subtitle"></div>
          </div>
          <div className="skeleton-badge"></div>
        </div>
        <div className="skeleton-body">
          <div className="skeleton-line"></div>
          <div className="skeleton-line"></div>
          <div className="skeleton-line skeleton-short"></div>
        </div>
        <div className="skeleton-footer">
          <div className="skeleton-line skeleton-footer-line"></div>
        </div>
      </div>
    </div>
  );
};

const SkeletonLoader = ({ count = 5 }) => {
  return (
    <div className="skeleton-loader-container">
      {Array.from({ length: count }, (_, index) => (
        <FeedbackSkeleton key={index} />
      ))}
    </div>
  );
};

const XSurveyFeed = ({ feedbackData = [], isLoading = false, onRefresh, globalDateRange = null }) => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSentiment, setFilterSentiment] = useState([]);
  const [filterRating, setFilterRating] = useState([]);
  const [filterAgent, setFilterAgent] = useState([]);
  const [filterCategory, setFilterCategory] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [showDropdowns, setShowDropdowns] = useState({
    sentiment: false,
    rating: false,
    agent: false,
    category: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [showSurveyDetails, setShowSurveyDetails] = useState(false);
  // legacy single-agent export not used anymore (removed)
  const [reportAgents, setReportAgents] = useState([]);
  const [showReportPicker, setShowReportPicker] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const parent = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Setup auto-animate
  useEffect(() => {
    if (parent.current) {
      autoAnimate(parent.current, { duration: 300 });
    }
  }, []);

  // Reset animation when data changes
  useEffect(() => {
    if (!isLoading && feedbackData.length > 0) {
      setHasAnimated(false);
      // Trigger animation after a short delay
      setTimeout(() => setHasAnimated(true), 100);
    }
  }, [feedbackData, isLoading]);

  // Filter out the summary data and only keep feedback entries
  const actualFeedbackData = feedbackData.filter((item) => item && item.ticket_id);

  // Transform XSurvey API data to match Feed component structure
  const transformedFeedbackData = actualFeedbackData.map((item) => {
    try {
    // Extract scale from feedback_type (e.g., "survey_5" -> 5, "csat_10" -> 10)
    const feedbackTypeMatch = item.feedback_type?.match(/_(\d+)$/);
    const scaleFromType = feedbackTypeMatch
      ? parseInt(feedbackTypeMatch[1])
      : null;
    const actualScale = scaleFromType || item.star_type || 5; // Default to 5

    // Create a Date object for proper sorting
    const feedbackDate = item.feedback_received_time ? new Date(item.feedback_received_time) : new Date();

    return {
      id: item.ticket_id || `unknown_${Date.now()}`,
      customer: item.customer_email ? item.customer_email.split("@")[0] : "Unknown Customer", // Extract name from email
      customerEmail: item.customer_email || "",
      customerPhone: "", // Not available in API
      rating: item.rating || 0,
      maxScale: actualScale, // Add the actual scale for proper display
      comment:
        item.fd_components?.find((c) => c.type === "text")?.answer ||
        "No text feedback provided",
      // Store both the Date object for sorting and formatted string for display
      feedbackDate: feedbackDate, // For sorting
      timestamp: feedbackDate.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      sentiment: item.sentiment || "Unknown",
      // Agent name: prefer assignee_name, fallback to extracting from email
      agent: (() => {
        // First try assignee_name
        if (item.assignee_name && item.assignee_name.trim()) {
          return item.assignee_name.trim();
        }
        // Fallback to assignee_email or agent_email
        const email = item.assignee_email || item.agent_email || "";
        return email ? email.split("@")[0] : "Unknown Agent";
      })(),
      agentEmail: item.assignee_email || item.agent_email || "",
      ticket: `#${item.ticket_id || 'Unknown'}`,
      category: item.group_name || "Uncategorized",
      ticketsSolved:
        typeof item.total_tickets_solved === "number"
          ? item.total_tickets_solved
          : Number(item.total_tickets_solved) || 0,
      priority: "Medium", // Default since not in API
      resolutionTime: "N/A", // Not available in API
      tags: [item.feedback_type || "Unknown", item.sentiment || "Unknown"],
      surveyTemplate: {
        title: "Customer Satisfaction Survey",
        scoreQuestion: item.score_question || "Unknown", // Add score question identifier
        questions:
          item.fd_components?.map((component, index) => {
            if (!component) return null;
            // Extract all keys from the component
            const allKeys = Object.keys(component);

            // Find the question key (should start with 'q' and be followed by numbers)
            const questionKey = allKeys.find((key) => /^q\d+$/.test(key));
            const questionText = questionKey
              ? component[questionKey]
              : "Unknown Question";
            const isScoreQuestion = questionKey === item.score_question; // Check if this is the score question

            // Enhanced debug logging for score question detection

            return {
              id: index + 1,
              questionKey: questionKey, // Add question key for score identification
              question: questionText,
              isScoreQuestion: isScoreQuestion, // Flag to identify score question
              type:
                component.type === "graphic" && component.label.includes("star")
                  ? "stars"
                  : component.type === "graphic" &&
                    component.label.includes("smiley")
                  ? "smileys"
                  : component.type === "graphic" &&
                    component.label.includes("Thumbs")
                  ? "thumbs"
                  : component.type === "numeric"
                  ? "scale"
                  : "text",
              maxRating: (() => {
                // Extract scale from feedback_type (e.g., "survey_5" -> 5, "csat_10" -> 10)
                const feedbackTypeMatch = item.feedback_type?.match(/_(\d+)$/);
                const scaleFromType = feedbackTypeMatch
                  ? parseInt(feedbackTypeMatch[1])
                  : null;

                // Use star_type as fallback
                const scaleFromStarType = item.star_type;

                // Determine the actual scale
                const actualScale = scaleFromType || scaleFromStarType || 5; // Default to 5

                if (
                  component.type === "graphic" &&
                  component.label.includes("star")
                ) {
                  return actualScale;
                } else if (
                  component.type === "graphic" &&
                  component.label.includes("smiley")
                ) {
                  return actualScale;
                } else if (component.type === "numeric") {
                  return actualScale;
                } else if (
                  component.type === "graphic" &&
                  component.label.includes("Thumbs")
                ) {
                  return 2;
                }
                return actualScale;
              })(),
              label: component.label,
              answer: component.answer,
              required: true,
            };
          }).filter(Boolean) || [],
      },
    };
    } catch (error) {
      console.error('Error transforming feedback data:', error, item);
      // Return a safe fallback object
      return {
        id: `error_${Date.now()}`,
        customer: "Error",
        customerEmail: "",
        customerPhone: "",
        rating: 0,
        maxScale: 5,
        comment: "Error processing feedback data",
        feedbackDate: new Date(),
        timestamp: new Date().toLocaleString(),
        sentiment: "Unknown",
        agent: "Unknown",
        agentEmail: "",
        ticket: "#Error",
        category: "Error",
        priority: "Medium",
        resolutionTime: "N/A",
        tags: ["Error"],
        surveyTemplate: {
          title: "Error",
          scoreQuestion: "Unknown",
          questions: []
        }
      };
    }
  });

  // Sort feedback data by date and time (latest first)
  const sortedFeedbackData = [...transformedFeedbackData].sort((a, b) => {
    try {
      return b.feedbackDate - a.feedbackDate; // Sort in descending order (newest first)
    } catch (error) {
      console.error('Error sorting feedback data:', error);
      return 0; // Keep original order if sorting fails
    }
  });

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "satisfied":
        return { icon: faSmile, color: "success", bgColor: "bg-success-soft" };
      case "dissatisfied":
        return { icon: faFrown, color: "danger", bgColor: "bg-danger-soft" };
      default:
        return { icon: faMeh, color: "warning", bgColor: "bg-warning-soft" };
    }
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FontAwesomeIcon
        key={index}
        icon={faStar}
        className={`${index < rating ? "text-warning filled" : "text-300"}`}
      />
    ));
  };

  const renderSurveyAnswer = (question) => {
    // Use the maxRating from the question which now contains the correct scale
    const scaleMax = question.maxRating || 5;
    const benchmark = {
      min: scaleMax === 5 ? 2 : 4,
      max: scaleMax === 5 ? 4 : 7,
    };

    switch (question.type) {
      case "stars":
        return (
          <div className="border-0 rounded-3 p-3 mb-1 bg-white shadow-sm text-center">
            <div className="d-flex justify-content-center flex-wrap gap-2">
              {[...Array(scaleMax)].map((_, i) => (
                <div
                  key={i}
                  className={`p-2 rounded-2 cursor-pointer transition-all ${
                    i + 1 <= question.answer
                      ? "bg-primary bg-opacity-10"
                      : "hover-bg-light"
                  }`}
                >
                  <img
                    src={i + 1 <= question.answer ? starBlack : starSvg}
                    style={{ width: "26px", height: "26px" }}
                    alt="star"
                  />
                </div>
              ))}
            </div>
            <div className="mt-2">
              <span
                className="badge bg-light text-muted px-2 py-1"
                style={{ fontSize: "11px", fontWeight: "500" }}
              >
                {question.answer}/{scaleMax}
              </span>
            </div>
          </div>
        );

      case "smileys":
        return (
          <div className="border-0 rounded-3 p-3 mb-1 bg-white shadow-sm text-center">
            <div className="d-flex justify-content-center flex-wrap gap-2">
              {smileys.map((icon, i) => {
                const number = i + 1;
                const isSelected = question.answer && number <= question.answer;
                let colorClass = "";
                if (number <= benchmark.min) {
                  colorClass = "bad-bg";
                } else if (number < benchmark.max) {
                  colorClass = "average-bg";
                } else {
                  colorClass = "good-bg";
                }

                return (
                  <div
                    key={i}
                    className={`p-2 rounded-2 cursor-pointer transition-all ${
                      isSelected ? colorClass : ""
                    }`}
                    style={{
                      border: `1px solid ${
                        isSelected
                          ? "transparent"
                          : number <= benchmark.min
                          ? "#fecaca"
                          : number < benchmark.max
                          ? "#fed7aa"
                          : "#bbf7d0"
                      }`,
                      backgroundColor: isSelected
                        ? number <= benchmark.min
                          ? "#fee2e2"
                          : number < benchmark.max
                          ? "#ffedd5"
                          : "#dcfce7"
                        : "transparent",
                    }}
                  >
                    <img
                      src={isSelected ? smileysBlack[i] : icon}
                      style={{ width: "26px", height: "26px" }}
                      alt={`smiley-${i}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-2">
              <span
                className="badge bg-light text-muted px-2 py-1"
                style={{ fontSize: "11px", fontWeight: "500" }}
              >
                {question.answer}/{scaleMax}
              </span>
            </div>
          </div>
        );

      case "scale":
      case "numeric": {
        return (
          <div className="border-0 rounded-3 p-3 mb-1 bg-white shadow-sm text-center">
            <div className="d-flex justify-content-center flex-wrap gap-2">
              {Array.from({ length: scaleMax }, (_, i) => i + 1).map(
                (number) => {
                  const isSelected =
                    question.answer && number <= question.answer;
                  let colorClass = "";
                  if (number <= benchmark.min) {
                    colorClass = "bad-bg";
                  } else if (number < benchmark.max) {
                    colorClass = "average-bg";
                  } else {
                    colorClass = "good-bg";
                  }

                  return (
                    <div
                      key={number}
                      className={`p-2 rounded-2 cursor-pointer transition-all ${
                        isSelected ? colorClass : ""
                      }`}
                      style={{
                        border: `1px solid ${
                          isSelected
                            ? "transparent"
                            : number <= benchmark.min
                            ? "#fecaca"
                            : number < benchmark.max
                            ? "#fed7aa"
                            : "#bbf7d0"
                        }`,
                        backgroundColor: isSelected
                          ? number <= benchmark.min
                            ? "#fee2e2"
                            : number < benchmark.max
                            ? "#ffedd5"
                            : "#dcfce7"
                          : "transparent",
                      }}
                    >
                      <span
                        className="fw-bold d-block"
                        style={{
                          color:
                            number <= benchmark.min
                              ? "#ef4444"
                              : number < benchmark.max
                              ? "#f97316"
                              : "#22c55e",
                          fontSize: "18px",
                          width: "26px",
                          height: "26px",
                          lineHeight: "26px",
                        }}
                      >
                        {number}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
            <div className="mt-2">
              <span
                className="badge bg-light text-muted px-2 py-1"
                style={{ fontSize: "11px", fontWeight: "500" }}
              >
                {question.answer}/{scaleMax}
              </span>
            </div>
          </div>
        );
      }

      case "thumbs":
        return (
          <div className="border-0 rounded-3 p-3 mb-1 bg-white shadow-sm text-center">
            <div className="d-flex justify-content-center gap-3">
              <div
                className={`p-2 rounded-2 cursor-pointer transition-all ${
                  question.answer === 1
                    ? "bg-primary bg-opacity-10"
                    : "hover-bg-light"
                }`}
              >
                <img
                  src={question.answer === 1 ? thumbUpBlack : thumbUpSvg}
                  alt="thumbs up"
                  style={{ width: "26px", height: "26px" }}
                />
              </div>
              <div
                className={`p-2 rounded-2 cursor-pointer transition-all ${
                  question.answer === 2
                    ? "bg-danger bg-opacity-10"
                    : "hover-bg-light"
                }`}
              >
                <img
                  src={question.answer === 2 ? thumbDownBlack : thumbDownSvg}
                  alt="thumbs down"
                  style={{ width: "26px", height: "26px" }}
                />
              </div>
            </div>
            <div className="mt-2">
              <span
                className="badge bg-light text-muted px-2 py-1"
                style={{ fontSize: "11px", fontWeight: "500" }}
              >
                {question.answer === 1
                  ? "Thumbs Up"
                  : question.answer === 2
                  ? "Thumbs Down"
                  : "No Answer"}
              </span>
            </div>
          </div>
        );

      case "text":
        return (
          <div className="border-0 rounded-3 p-3 mb-1 bg-white shadow-sm">
            <div className="mx-auto">
              <div
                className="border-1"
                style={{
                  borderRadius: "10px",
                  fontSize: "14px",
                  borderColor: "#e0e0e0",
                  minHeight: "90px",
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {question.answer}
              </div>
            </div>
          </div>
        );

      default:
        return <span>{question.answer}</span>;
    }
  };

  const getCustomerInitial = (customerName) => {
    return customerName.charAt(0).toUpperCase();
  };

  const handleFeedbackClick = (feedback) => {
    setSelectedFeedback(feedback);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFeedback(null);
    setShowSurveyDetails(false);
  };

  const exportToCSV = () => {
    const headers = [
      "Customer",
      "Ticket",
      "Rating",
      "Sentiment",
      "Agent",
      "Category",
      "Priority",
      "Comment",
      "Timestamp",
    ];
    const csvContent = [
      headers.join(","),
      ...filteredFeedback.map((feedback) =>
        [
          feedback.customer,
          feedback.ticket,
          feedback.rating,
          feedback.sentiment,
          feedback.agent,
          feedback.category,
          feedback.priority,
          `"${feedback.comment.replace(/"/g, '""')}"`,
          feedback.timestamp,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `feedback_export_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get unique agents and categories for filter options
  // Build agent map: email -> best name (prefer assignee_name)
  const agentMap = new Map();
  
  // First pass: collect all agents with their emails
  // Process items with assignee_name first, then those without
  const itemsWithName = actualFeedbackData.filter(item => item.assignee_name && item.assignee_name.trim());
  const itemsWithoutName = actualFeedbackData.filter(item => !item.assignee_name || !item.assignee_name.trim());
  
  // Process items with assignee_name first (higher priority)
  [...itemsWithName, ...itemsWithoutName].forEach((item) => {
    const email = item.assignee_email || item.agent_email || "";
    if (!email) return;
    
    // Determine the best name for this email
    let bestName = null;
    if (item.assignee_name && item.assignee_name.trim()) {
      bestName = item.assignee_name.trim();
    } else {
      bestName = email.split("@")[0];
    }
    
    // Only set if we don't have this email yet, or if this item has assignee_name (preferred)
    const hasName = item.assignee_name && item.assignee_name.trim();
    if (!agentMap.has(email) || hasName) {
      agentMap.set(email, bestName);
    }
  });
  
  // Convert map to array of unique agent names, sorted alphabetically
  const uniqueAgents = Array.from(agentMap.values()).sort();
  
  // Create a reverse map: agent name -> email for filtering
  // Since we deduplicated by email, each name should map to exactly one email
  const agentNameToEmail = new Map();
  agentMap.forEach((name, email) => {
    agentNameToEmail.set(name, email);
  });
  
  const uniqueCategories = [
    ...new Set(sortedFeedbackData.map((feedback) => feedback.category)),
  ];

  

  // Filter feedback based on all criteria
  const filteredFeedback = sortedFeedbackData.filter((feedback) => {
    const matchesSearch =
      feedback.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.comment.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.agent.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feedback.ticket.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSentiment =
      filterSentiment.length === 0 || filterSentiment.includes(feedback.sentiment);
    const matchesRating =
      filterRating.length === 0 || filterRating.includes(feedback.rating.toString());
    const matchesAgent = (() => {
      if (filterAgent.length === 0) return true;
      // Get the email for this feedback's agent name
      const agentEmail = feedback.agentEmail;
      // Check if any selected agent name maps to this email
      return filterAgent.some((selectedAgentName) => {
        const selectedEmail = agentNameToEmail.get(selectedAgentName);
        return selectedEmail === agentEmail;
      });
    })();
    const matchesCategory =
      filterCategory.length === 0 || filterCategory.includes(feedback.category);

    // Date range filtering
    let matchesDateRange = true;
    if (dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      // Use the feedbackDate for proper date comparison
      matchesDateRange = feedback.feedbackDate >= startDate && feedback.feedbackDate <= endDate;
    }

    return (
      matchesSearch &&
      matchesSentiment &&
      matchesRating &&
      matchesAgent &&
      matchesCategory &&
      matchesDateRange
    );
  });

  // Calculate closed tickets correctly by getting unique agent totals
  // Each feedback item has total_tickets_solved which represents the agent's total closures
  // We need to get unique totals per agent and sum them, not sum all feedback items
  // This matches the logic used in computeAgentMetrics for the report
  
  const getClosureValue = (entry) => {
    const raw =
      entry?.ticketsSolved ??
      entry?.totalTicketsSolved ??
      entry?.total_tickets_solved ??
      0;
    const numeric = Number(raw);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
  };
  
  const normalizeClosures = (values = []) => {
    if (!values || values.length === 0) return 0;
    // If all values are the same, return that value (common case)
    // Otherwise sum them (shouldn't happen for same agent, but handle edge cases)
    return values.every((val) => val === values[0])
      ? values[0]
      : values.reduce((sum, val) => sum + val, 0);
  };
  
  // Group closures by agent (using email as key)
  const agentClosuresMap = new Map();
  
  filteredFeedback.forEach((feedback) => {
    const agentKey = feedback?.agentEmail || feedback?.assignee_email || feedback?.agent || "Unknown";
    const closureValue = getClosureValue(feedback);
    
    if (closureValue > 0) {
      // Collect all closure values for this agent
      if (!agentClosuresMap.has(agentKey)) {
        agentClosuresMap.set(agentKey, []);
      }
      agentClosuresMap.get(agentKey).push(closureValue);
    }
  });

  // Normalize closures per agent (same logic as computeAgentMetrics)
  // Then sum all unique agent totals
  const closedTicketsCount = Array.from(agentClosuresMap.values())
    .map(closureValues => normalizeClosures(closureValues))
    .reduce((sum, agentTotal) => sum + agentTotal, 0);

  // Calculate summary statistics
  const positiveCount = filteredFeedback.filter((f) => f.sentiment === "satisfied").length;
  const totalCount = filteredFeedback.length;
  
  const summaryStats = {
    totalFeedback: totalCount,
    averageRating:
      filteredFeedback.length > 0
        ? (
            filteredFeedback.reduce(
              (sum, feedback) => sum + feedback.rating,
              0
            ) / filteredFeedback.length
          ).toFixed(1)
        : 0,
    averageScale:
      filteredFeedback.length > 0
        ? Math.round(
            filteredFeedback.reduce(
              (sum, feedback) => sum + (feedback.maxScale || 5),
              0
            ) / filteredFeedback.length
          )
        : 5,
    sentimentDistribution: {
      positive: positiveCount,
      negative: filteredFeedback.filter((f) => f.sentiment === "dissatisfied")
        .length,
      neutral: filteredFeedback.filter((f) => f.sentiment === "neutral").length,
    },
    ticketsSolved: closedTicketsCount,
    csat: totalCount > 0 ? (positiveCount / totalCount) * 100 : 0,
  };

  // Compute agent-wise metrics for PDF report (after filteredFeedback is available)
  const computeAgentMetrics = React.useMemo(() => {
    const result = {};
    const allCategoriesSet = new Set(); // Collect all unique categories dynamically
    
    // Build email-to-name mapping: prefer assignee_name, fallback to email prefix
    // This ensures all tickets with the same email are grouped together
    const emailToNameMap = new Map();
    
    // First pass: collect all agents with their emails, prioritizing items with names
    const itemsWithName = filteredFeedback.filter(f => f.agent && f.agentEmail && f.agent !== f.agentEmail.split("@")[0]);
    const itemsWithoutName = filteredFeedback.filter(f => !f.agentEmail || f.agent === (f.agentEmail ? f.agentEmail.split("@")[0] : "Unknown"));
    
    // Process items with proper names first (higher priority)
    [...itemsWithName, ...itemsWithoutName].forEach((f) => {
      const email = f.agentEmail || "";
      if (!email) return;
      
      // Determine the best name for this email
      let bestName = f.agent || email.split("@")[0] || "Unknown";
      
      // Only set if we don't have this email yet, or if this item has a proper name (not just email prefix)
      const hasProperName = f.agent && f.agent !== email.split("@")[0];
      if (!emailToNameMap.has(email) || hasProperName) {
        emailToNameMap.set(email, bestName);
      }
    });
    
    const getClosureValue = (entry) => {
      const raw =
        entry?.ticketsSolved ??
        entry?.totalTicketsSolved ??
        entry?.total_tickets_solved ??
        0;
      const numeric = Number(raw);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
    };
    const normalizeClosures = (values = []) => {
      if (!values || values.length === 0) return 0;
      return values.every((val) => val === values[0])
        ? values[0]
        : values.reduce((sum, val) => sum + val, 0);
    };

    // Group by email instead of name to consolidate same agent
    filteredFeedback.forEach((f) => {
      const agentEmail = f.agentEmail || "";
      // Use email as the key for grouping, fallback to agent name if no email
      const agentKey = agentEmail || f.agent || "Unknown";
      
      // Get the best display name for this email
      const displayName = agentEmail && emailToNameMap.has(agentEmail) 
        ? emailToNameMap.get(agentEmail) 
        : (f.agent || agentEmail.split("@")[0] || "Unknown");
      
      if (!result[agentKey]) {
        result[agentKey] = {
          displayName: displayName, // Store the best name for display
          categories: {},
          overall: {
            total: 0,
            positive: 0,
            csat: 0,
            avgRatingSum: 0,
            avgRatingCount: 0,
            closuresRaw: [],
          },
        };
      }
      
      // Update display name if we found a better one (prefer names over email prefixes)
      const currentName = result[agentKey].displayName;
      const hasBetterName = displayName && displayName !== agentEmail.split("@")[0] && currentName === agentEmail.split("@")[0];
      if (hasBetterName) {
        result[agentKey].displayName = displayName;
      }
      const a = result[agentKey];
      // Use the actual category name from data (dynamic)
      const catName = f.category ? f.category.toString().trim() : null;
      if (catName) {
        allCategoriesSet.add(catName); // Track all unique categories
        if (!a.categories[catName]) {
          a.categories[catName] = {
            total: 0,
            positive: 0,
            csat: 0,
            avgRatingSum: 0,
            avgRatingCount: 0,
            closuresRaw: [],
          };
        }
        a.categories[catName].total += 1;
        a.categories[catName].positive += f.sentiment === "satisfied" ? 1 : 0;
        a.categories[catName].avgRatingSum += Number(f.rating || 0);
        a.categories[catName].avgRatingCount += 1;
      }
      a.overall.total += 1;
      a.overall.positive += f.sentiment === "satisfied" ? 1 : 0;
      a.overall.avgRatingSum += Number(f.rating || 0);
      a.overall.avgRatingCount += 1;

      // Collect total_tickets_solved only for overall (it represents total closures for the agent across all groups)
      // We'll distribute it proportionally to categories later based on survey distribution
      const closureValue = getClosureValue(f);
      if (closureValue > 0) {
        // Only add to overall, not to individual categories
        // The total_tickets_solved represents the agent's total closures across ALL groups
        a.overall.closuresRaw.push(closureValue);
        // DO NOT add to category closuresRaw - we'll calculate category closures proportionally
      }
    });

    Object.values(result).forEach((a) => {
      Object.values(a.categories).forEach((c) => {
        c.csat = c.total > 0 ? (c.positive / c.total) * 100 : 0;
        c.avgRating = c.avgRatingCount > 0 ? c.avgRatingSum / c.avgRatingCount : 0;
      });
      a.overall.csat = a.overall.total > 0 ? (a.overall.positive / a.overall.total) * 100 : 0;
      a.overall.avgRating = a.overall.avgRatingCount > 0 ? a.overall.avgRatingSum / a.overall.avgRatingCount : 0;

      // Get the overall closures (total_tickets_solved for this agent)
      const overallClosures = normalizeClosures(a.overall.closuresRaw);
      a.overall.closures = overallClosures;
      delete a.overall.closuresRaw;

      // Distribute closures proportionally to each category based on survey distribution
      // This ensures that if an agent has 150 total closures and 4 surveys in AskHR and 13 in GW Support,
      // the closures are split proportionally: AskHR gets ~35, GW Support gets ~115
      Object.values(a.categories).forEach((c) => {
        delete c.closuresRaw; // Clean up - we don't use category closuresRaw anymore
        
        // Always calculate category closures proportionally based on survey distribution
        if (overallClosures > 0 && a.overall.total > 0 && c.total > 0) {
          // Calculate proportional closures: (category surveys / total surveys) * overall closures
          c.closures = Math.round((c.total / a.overall.total) * overallClosures);
        } else {
          c.closures = 0;
        }
      });
    });
    // Convert Set to sorted array for consistent ordering
    const categoriesOfInterest = Array.from(allCategoriesSet).sort();
    return { agents: result, categoriesOfInterest };
  }, [filteredFeedback]);

  // remove unused legacy exporter (kept as comment)
  /* const exportAgentReportToPdf = async (targetAgent) => {
    const { agents, categoriesOfInterest } = computeAgentMetrics;
    const names = targetAgent ? [targetAgent] : Object.keys(agents);
    if (names.length === 0) return;
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-100000px";
    container.style.top = "0";
    container.style.width = "1400px";
    container.style.background = "#fff";
    container.style.padding = "16px";
    container.style.fontFamily = "Inter, Arial, sans-serif";
    const header = document.createElement("div");
    header.style.display = "flex";
    header.style.justifyContent = "space-between";
    header.style.marginBottom = "12px";
    header.innerHTML = `<div style="font-size:20px;font-weight:700;">Agent CSAT Report</div>
      <div style="font-size:12px;">Generated: ${new Date().toLocaleString()}</div>`;
    container.appendChild(header);
    names.forEach((n) => {
      const data = agents[n];
      const section = document.createElement("div");
      section.style.margin = "12px 0 20px";
      section.innerHTML = `<div style="font-weight:700;margin:6px 0;">${n}</div>`;
      const table = document.createElement("table");
      table.style.borderCollapse = "collapse";
      table.style.width = "100%";
      table.style.fontSize = "12px";
      const thead = document.createElement("thead");
      thead.innerHTML = `<tr>
        <th style="border:1px solid #ccc;padding:6px;text-align:left;">Section</th>
        <th style="border:1px solid #ccc;padding:6px;text-align:right;">Total Responses</th>
        <th style="border:1px solid #ccc;padding:6px;text-align:right;">Positive</th>
        <th style="border:1px solid #ccc;padding:6px;text-align:right;">CSAT</th>
        <th style="border:1px solid #ccc;padding:6px;text-align:right;">Avg Rating</th>
      </tr>`;
      table.appendChild(thead);
      const tbody = document.createElement("tbody");
      const rowFor = (label, obj) => {
        const tr = document.createElement("tr");
        tr.innerHTML = `
          <td style="border:1px solid #eee;padding:6px;">${label}</td>
          <td style="border:1px solid #eee;padding:6px;text-align:right;">${obj.total || 0}</td>
          <td style="border:1px solid #eee;padding:6px;text-align:right;">${obj.positive || 0}</td>
          <td style="border:1px solid #eee;padding:6px;text-align:right;">${(obj.csat || 0).toFixed(1)}%</td>
          <td style="border:1px solid #eee;padding:6px;text-align:right;">${(obj.avgRating || 0).toFixed(2)}</td>`;
        return tr;
      };
      categoriesOfInterest.forEach((cat) => {
        const c = (data.categories && data.categories[cat]) || { total: 0, positive: 0, csat: 0, avgRating: 0 };
        tbody.appendChild(rowFor(cat, c));
      });
      tbody.appendChild(rowFor("Overall", data.overall || { total: 0, positive: 0, csat: 0, avgRating: 0 }));
      table.appendChild(tbody);
      section.appendChild(table);
      container.appendChild(section);
    });
    document.body.appendChild(container);
    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save(targetAgent ? `agent_report_${targetAgent}.pdf` : "agent_report_all.pdf");
    document.body.removeChild(container);
  }; */

  // Generate report data for preview and download
  const generateReportData = React.useCallback(() => {
    const { agents, categoriesOfInterest } = computeAgentMetrics;
    
    // Convert agent names to email keys for lookup
    // If reportAgents is empty or null, use all agents; otherwise convert names to emails
    // Ensure we're using the current reportAgents state
    const currentReportAgents = reportAgents || [];
    const selectedEmailKeys = currentReportAgents.length > 0 
      ? currentReportAgents.map(name => agentNameToEmail.get(name) || name).filter(Boolean)
      : Object.keys(agents);
    
    if (selectedEmailKeys.length === 0) return null;

    const getSafe = (obj, key, def) => (obj && key in obj ? obj[key] : def);
    const calcFor = (emailKey, cat) => {
      const c = (agents[emailKey] && agents[emailKey].categories && agents[emailKey].categories[cat]) || {
        total: 0,
        positive: 0,
        csat: 0,
        avgRating: 0,
      };
      const closures = getSafe(c, "closures", 0);
      const surveys = getSafe(c, "total", 0);
      const srr = closures > 0 ? (surveys / closures) * 100 : 0;
      const positive = getSafe(c, "positive", 0);
      const csat = surveys > 0 ? (positive / surveys) * 100 : 0;
      return { closures, surveys, srr, positive, csat };
    };
    const calcOverall = (emailKey) => {
      const over = (agents[emailKey] && agents[emailKey].overall) || { total: 0, positive: 0 };
      const closures = getSafe(over, "closures", 0);
      const surveys = getSafe(over, "total", 0);
      const srr = closures > 0 ? (surveys / closures) * 100 : 0;
      const positive = getSafe(over, "positive", 0);
      const csat = surveys > 0 ? (positive / surveys) * 100 : 0;
      return { closures, surveys, srr, positive, csat };
    };

    const totalSurveysAllAgents = selectedEmailKeys.reduce((sum, emailKey) => sum + getSafe(agents[emailKey]?.overall, "total", 0), 0);

    // Build agent rows with dynamic categories
    const agentRows = selectedEmailKeys.map((emailKey) => {
      const displayName = agents[emailKey]?.displayName || emailKey.split("@")[0] || "Unknown";
      const ov = calcOverall(emailKey);
      const load = totalSurveysAllAgents > 0 ? (ov.surveys / totalSurveysAllAgents) * 100 : 0;
      
      // Calculate metrics for each dynamic category
      const categoryData = {};
      categoriesOfInterest.forEach(cat => {
        categoryData[cat] = calcFor(emailKey, cat);
      });
      
      return { name: displayName, categories: categoryData, overall: ov, load };
    });

    // Calculate totals for each category dynamically
    const totals = {};
    categoriesOfInterest.forEach(cat => {
      totals[cat] = { closures: 0, surveys: 0, positive: 0 };
    });
    totals.overall = { closures: 0, surveys: 0, positive: 0 };

    agentRows.forEach((row) => {
      categoriesOfInterest.forEach(cat => {
        totals[cat].closures += row.categories[cat].closures;
        totals[cat].surveys += row.categories[cat].surveys;
        totals[cat].positive += row.categories[cat].positive;
      });
      totals.overall.closures += row.overall.closures;
      totals.overall.surveys += row.overall.surveys;
      totals.overall.positive += row.overall.positive;
    });

    const srrFrom = (surveys, closures) => (closures > 0 ? (surveys / closures) * 100 : 0);
    const csatFrom = (positive, surveys) => (surveys > 0 ? (positive / surveys) * 100 : 0);

    // Build totals row dynamically
    const totalsRow = {};
    categoriesOfInterest.forEach(cat => {
      totalsRow[cat] = {
        closures: totals[cat].closures,
        surveys: totals[cat].surveys,
        srr: srrFrom(totals[cat].surveys, totals[cat].closures),
        positive: totals[cat].positive,
        csat: csatFrom(totals[cat].positive, totals[cat].surveys),
      };
    });
    totalsRow.overall = {
      closures: totals.overall.closures,
      surveys: totals.overall.surveys,
      srr: srrFrom(totals.overall.surveys, totals.overall.closures),
      positive: totals.overall.positive,
      csat: csatFrom(totals.overall.positive, totals.overall.surveys),
    };

    // Format date range for display - use global date range from top date picker
    const formatDateRange = () => {
      if (!globalDateRange || !Array.isArray(globalDateRange) || globalDateRange.length !== 2) {
        return "All Dates";
      }
      const [startDate, endDate] = globalDateRange;
      if (!startDate || !endDate) {
        return "All Dates";
      }
      const start = startDate instanceof Date ? startDate : new Date(startDate);
      const end = endDate instanceof Date ? endDate : new Date(endDate);
      const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      return `${formatDate(start)} - ${formatDate(end)}`;
    };

    return {
      agentRows,
      totalsRow,
      categories: categoriesOfInterest, // Include categories for dynamic rendering
      generatedAt: new Date().toLocaleString(),
      dateRange: formatDateRange(),
      dateRangeStart: globalDateRange && Array.isArray(globalDateRange) && globalDateRange[0] ? globalDateRange[0] : null,
      dateRangeEnd: globalDateRange && Array.isArray(globalDateRange) && globalDateRange[1] ? globalDateRange[1] : null,
    };
  }, [reportAgents, computeAgentMetrics, agentNameToEmail, globalDateRange]);

  // Handle opening report preview modal
  const handleGenerateReport = () => {
    const data = generateReportData();
    if (data) {
      setReportData(data);
      setShowReportModal(true);
    }
  };

  // New: Export multi-agent matrix report PDF matching requested columns/groups
  const exportAgentMatrixReportPdf = async () => {
    const { agents, categoriesOfInterest } = computeAgentMetrics;
    
    // Convert agent names to email keys for lookup
    const selectedEmailKeys = reportAgents.length > 0 
      ? reportAgents.map(name => agentNameToEmail.get(name) || name).filter(Boolean)
      : Object.keys(agents);
    
    if (selectedEmailKeys.length === 0) return;

    // Build aggregate totals for Overall Ticket Load and totals row
    const getSafe = (obj, key, def) => (obj && key in obj ? obj[key] : def);
    const calcFor = (emailKey, cat) => {
      const c = (agents[emailKey] && agents[emailKey].categories && agents[emailKey].categories[cat]) || {
        total: 0,
        positive: 0,
        csat: 0,
        avgRating: 0,
      };
      const closures = getSafe(c, "closures", 0);
      const surveys = getSafe(c, "total", 0);
      const srr = closures > 0 ? (surveys / closures) * 100 : 0;
      const positive = getSafe(c, "positive", 0);
      const csat = surveys > 0 ? (positive / surveys) * 100 : 0;
      return { closures, surveys, srr, positive, csat };
    };
    const calcOverall = (emailKey) => {
      const over = (agents[emailKey] && agents[emailKey].overall) || { total: 0, positive: 0 };
      const closures = getSafe(over, "closures", 0);
      const surveys = getSafe(over, "total", 0);
      const srr = closures > 0 ? (surveys / closures) * 100 : 0;
      const positive = getSafe(over, "positive", 0);
      const csat = surveys > 0 ? (positive / surveys) * 100 : 0;
      return { closures, surveys, srr, positive, csat };
    };

    const totalSurveysAllAgents = selectedEmailKeys.reduce((sum, emailKey) => sum + getSafe(agents[emailKey]?.overall, "total", 0), 0);

    // Render table off-screen
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-100000px";
    container.style.top = "0";
    container.style.width = "1800px";
    container.style.background = "#fff";
    container.style.padding = "16px";
    container.style.fontFamily = "Inter, Arial, sans-serif";

    // Format date range for PDF - use global date range from top date picker
    const formatDateRangeForPdf = () => {
      if (!globalDateRange || !Array.isArray(globalDateRange) || globalDateRange.length !== 2) {
        return "All Dates";
      }
      const [startDate, endDate] = globalDateRange;
      if (!startDate || !endDate) {
        return "All Dates";
      }
      const start = startDate instanceof Date ? startDate : new Date(startDate);
      const end = endDate instanceof Date ? endDate : new Date(endDate);
      const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      return `${formatDate(start)} - ${formatDate(end)}`;
    };

    const header = document.createElement("div");
    header.style.marginBottom = "16px";
    header.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
        <div style="font-size:20px;font-weight:700;">Agent Survey Report</div>
        <div style="font-size:12px;text-align:right;">Generated: ${new Date().toLocaleString()}</div>
      </div>
      <div style="display:flex;align-items:center;gap:6px;font-size:12px;color:#6c757d;margin-top:4px;">
        <span style="font-weight:600;color:#495057;">Report Period:</span>
        <span>${formatDateRangeForPdf()}</span>
      </div>
    `;
    container.appendChild(header);

    const table = document.createElement("table");
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";
    table.style.fontSize = "12px";

    // Header row
    const thead = document.createElement("thead");
    const headerRow1 = document.createElement("tr");
    const mkTh = (text, colspan) => {
      const th = document.createElement("th");
      th.style.border = "1px solid #ccc";
      th.style.padding = "6px";
      th.style.textAlign = "center";
      th.style.background = "#f8f9fa";
      th.colSpan = colspan || 1;
      th.textContent = text;
      return th;
    };
    // Generate colors for categories dynamically
    const getCategoryColor = (index) => {
      const colors = ["#e3f2fd", "#fff3e0", "#f3e5f5", "#e8f5e9", "#fce4ec", "#e0f2f1", "#fff9c4", "#f1f8e9"];
      return colors[index % colors.length];
    };

    headerRow1.appendChild(mkTh("Agent", 1));
    // Add dynamic category columns
    categoriesOfInterest.forEach((cat, idx) => {
      const th = mkTh(cat, 5);
      th.style.background = getCategoryColor(idx);
      headerRow1.appendChild(th);
    });
    headerRow1.appendChild(mkTh("Overall", 5));
    headerRow1.appendChild(mkTh("Overall Ticket Load", 1));
    thead.appendChild(headerRow1);

    const headerRow2 = document.createElement("tr");
    const addGroupCols = () => {
      ["Total Closures", "Total Surveys", "SRR", "Positive", "CSAT"].forEach((t) => {
        const th = document.createElement("th");
        th.style.border = "1px solid #ccc";
        th.style.padding = "6px";
        th.style.textAlign = "center";
        th.textContent = t;
        headerRow2.appendChild(th);
      });
    };
    // spacer under Agent
    const thAgentSpacer = document.createElement("th");
    thAgentSpacer.style.border = "1px solid #ccc";
    thAgentSpacer.style.padding = "6px";
    thAgentSpacer.style.textAlign = "center";
    thAgentSpacer.textContent = "Name";
    headerRow2.appendChild(thAgentSpacer);
    // Add column headers for each dynamic category
    categoriesOfInterest.forEach(() => {
      addGroupCols();
    });
    addGroupCols(); // Overall columns
    // Ticket load column header
    const thLoad = document.createElement("th");
    thLoad.style.border = "1px solid #ccc";
    thLoad.style.padding = "6px";
    thLoad.style.textAlign = "center";
    thLoad.textContent = "%";
    headerRow2.appendChild(thLoad);
    thead.appendChild(headerRow2);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    const mkTd = (text, alignRight, bgColor) => {
      const td = document.createElement("td");
      td.style.border = "1px solid #eee";
      td.style.padding = "6px";
      td.style.textAlign = alignRight ? "right" : "left";
      if (bgColor) {
        td.style.backgroundColor = bgColor;
      }
      td.textContent = text;
      return td;
    };

    // Helper to get CSAT color based on value
    const getCSATColor = (csatValue) => {
      if (csatValue >= 90) return "#d4edda"; // light green
      if (csatValue < 80) return "#f8d7da"; // light red
      return null; // no color for 80-90%
    };

    // Initialize totals dynamically for all categories
    const totals = {};
    categoriesOfInterest.forEach(cat => {
      totals[cat] = { closures: 0, surveys: 0, positive: 0 };
    });
    totals.overall = { closures: 0, surveys: 0, positive: 0 };

    selectedEmailKeys.forEach((emailKey) => {
      const displayName = agents[emailKey]?.displayName || emailKey.split("@")[0] || "Unknown";
      const ov = calcOverall(emailKey);
      const load = totalSurveysAllAgents > 0 ? (ov.surveys / totalSurveysAllAgents) * 100 : 0;

      // Calculate metrics for each dynamic category
      const categoryData = {};
      categoriesOfInterest.forEach(cat => {
        categoryData[cat] = calcFor(emailKey, cat);
        totals[cat].closures += categoryData[cat].closures;
        totals[cat].surveys += categoryData[cat].surveys;
        totals[cat].positive += categoryData[cat].positive;
      });

      totals.overall.closures += ov.closures;
      totals.overall.surveys += ov.surveys;
      totals.overall.positive += ov.positive;

      const tr = document.createElement("tr");
      tr.appendChild(mkTd(displayName, false));
      
      // Add columns for each dynamic category
      categoriesOfInterest.forEach(cat => {
        const catData = categoryData[cat];
        tr.appendChild(mkTd(String(catData.closures), true));
        tr.appendChild(mkTd(String(catData.surveys), true));
        tr.appendChild(mkTd(`${catData.srr.toFixed(0)}%`, true));
        tr.appendChild(mkTd(String(catData.positive), true));
        const csatColor = getCSATColor(catData.csat);
        tr.appendChild(mkTd(`${catData.csat.toFixed(1)}%`, true, csatColor));
      });
      
      // Overall columns
      tr.appendChild(mkTd(String(ov.closures), true));
      tr.appendChild(mkTd(String(ov.surveys), true));
      tr.appendChild(mkTd(`${ov.srr.toFixed(0)}%`, true));
      tr.appendChild(mkTd(String(ov.positive), true));
      const ovCsatColor = getCSATColor(ov.csat);
      tr.appendChild(mkTd(`${ov.csat.toFixed(1)}%`, true, ovCsatColor));
      
      tr.appendChild(mkTd(`${load.toFixed(0)}%`, true));
      tbody.appendChild(tr);
    });

    // Totals row
    const trTotal = document.createElement("tr");
    trTotal.style.fontWeight = "700";
    trTotal.appendChild(mkTd("Total", false));
    const srrFrom = (surveys, closures) => (closures > 0 ? (surveys / closures) * 100 : 0);
    const csatFrom = (positive, surveys) => (surveys > 0 ? (positive / surveys) * 100 : 0);

    const pushTotals = (agg) => {
      const closures = agg.closures;
      const surveys = agg.surveys;
      const positive = agg.positive;
      const csatVal = csatFrom(positive, surveys);
      trTotal.appendChild(mkTd(String(closures), true));
      trTotal.appendChild(mkTd(String(surveys), true));
      trTotal.appendChild(mkTd(`${srrFrom(surveys, closures).toFixed(0)}%`, true));
      trTotal.appendChild(mkTd(String(positive), true));
      const csatColor = getCSATColor(csatVal);
      trTotal.appendChild(mkTd(`${csatVal.toFixed(1)}%`, true, csatColor));
    };
    
    // Push totals for each dynamic category
    categoriesOfInterest.forEach(cat => {
      pushTotals(totals[cat]);
    });
    pushTotals(totals.overall);
    trTotal.appendChild(mkTd("100%", true));
    tbody.appendChild(trTotal);

    table.appendChild(tbody);
    container.appendChild(table);

    document.body.appendChild(container);
    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save("agent_matrix_report.pdf");
    document.body.removeChild(container);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredFeedback.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFeedback = filteredFeedback.slice(startIndex, endIndex);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    filterSentiment,
    filterRating,
    filterAgent,
    filterCategory,
    dateRange,
    itemsPerPage,
  ]);

  // Helper functions for multi-select dropdowns
  const toggleFilterOption = (filterType, value) => {
    if (filterType === "sentiment") {
      setFilterSentiment((prev) =>
        prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value]
      );
    } else if (filterType === "rating") {
      setFilterRating((prev) =>
        prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value]
      );
    } else if (filterType === "agent") {
      setFilterAgent((prev) =>
        prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value]
      );
    } else if (filterType === "category") {
      setFilterCategory((prev) =>
        prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value]
      );
    }
  };

  const getFilterDisplayText = (filterType) => {
    if (filterType === "sentiment") {
      if (filterSentiment.length === 0) return "All Sentiments";
      if (filterSentiment.length === 1) return filterSentiment[0];
      return `${filterSentiment.length} Selected`;
    } else if (filterType === "rating") {
      if (filterRating.length === 0) return "All Ratings";
      if (filterRating.length === 1) return `${filterRating[0]} Stars`;
      return `${filterRating.length} Selected`;
    } else if (filterType === "agent") {
      if (filterAgent.length === 0) return "All Agents";
      if (filterAgent.length === 1) return filterAgent[0];
      return `${filterAgent.length} Selected`;
    } else if (filterType === "category") {
      if (filterCategory.length === 0) return "All Categories";
      if (filterCategory.length === 1) return filterCategory[0];
      return `${filterCategory.length} Selected`;
    }
    return "";
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="feed-container">
      <Card className="h-100 shadow-sm border-0 feed-card">
        <Card.Header className="feed-header border-0" style={{ borderBottom: "1px solid rgba(226, 232, 240, 0.8)" }}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)",
                }}
              >
                <FontAwesomeIcon
                  icon={faComments}
                  style={{ color: "#475569", fontSize: "16px" }}
                />
              </div>
              <div>
                <h5 
                  className="mb-1 fw-bold" 
                  style={{ 
                    fontSize: "0.95rem", 
                    color: "#0f172a",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    lineHeight: "1.4",
                    letterSpacing: "-0.01em"
                  }}
                >
                  Recent Feedback
                </h5>
                <span 
                  className="d-block" 
                  style={{ 
                    fontSize: "0.75rem", 
                    color: "#64748b",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    lineHeight: "1.5",
                    fontWeight: "400"
                  }}
                >
                  Latest customer responses (sorted by date & time)
                </span>
              </div>
            </div>
            <div className="d-flex gap-2 align-items-center" style={{ alignItems: "center" }}>
              {onRefresh && (
                <Button
                  variant="outline-secondary"
                  onClick={onRefresh}
                  disabled={isLoading}
                  className="d-flex align-items-center justify-content-center"
                  style={{ 
                    height: "38px",
                    padding: "0.375rem 0.75rem",
                    fontSize: "12px",
                    fontWeight: "normal",
                    color: "#495057",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    lineHeight: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  Refresh
                </Button>
              )}
              <Button
                variant="outline-secondary"
                onClick={exportToCSV}
                disabled={filteredFeedback.length === 0}
                className="d-flex align-items-center justify-content-center"
                style={{ 
                  height: "38px",
                  padding: "0.375rem 0.75rem",
                  fontSize: "12px",
                  fontWeight: "normal",
                  color: "#495057",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                Export CSV
              </Button>
              <Dropdown
                show={showReportPicker}
                onToggle={(open) => setShowReportPicker(open)}
              >
                <Dropdown.Toggle 
                  as={Button}
                  variant="outline-secondary"
                  className="d-flex align-items-center justify-content-between"
                  style={{ 
                    height: "38px",
                    padding: "0.375rem 0.75rem",
                    fontSize: "12px",
                    fontWeight: "normal",
                    color: "#495057",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    minWidth: "180px",
                    maxWidth: "280px",
                    lineHeight: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faUsers} className="me-2" style={{ fontSize: "12px" }} />
                    <span 
                      className={reportAgents.length > 0 ? "fw-semibold" : ""}
                      style={{ 
                        overflow: "hidden", 
                        textOverflow: "ellipsis", 
                        whiteSpace: "nowrap",
                        flex: 1,
                        textAlign: "left"
                      }}
                    >
                      {reportAgents.length === 0 
                        ? "All Agents" 
                        : reportAgents.length === 1 
                          ? reportAgents[0]
                          : `${reportAgents.length} Selected`}
                    </span>
                    {reportAgents.length > 0 && (
                      <Badge bg="dark" className="ms-2" style={{ fontSize: "0.65rem", padding: "0.15rem 0.4rem", flexShrink: 0 }}>
                        {reportAgents.length}
                      </Badge>
                    )}
                  </div>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className="ms-2"
                    style={{ 
                      fontSize: "12px", 
                      transition: "transform 0.2s ease",
                      transform: showReportPicker ? "rotate(180deg)" : "rotate(0deg)",
                      flexShrink: 0
                    }}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu 
                  className="agent-report-dropdown-menu" 
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    minWidth: "300px",
                    maxWidth: "350px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    padding: 0,
                    backgroundColor: "#ffffff",
                    background: "#ffffff"
                  }}
                >
                  <div className="agent-report-dropdown-header" style={{ 
                    position: "sticky", 
                    top: 0, 
                    background: "#ffffff", 
                    backgroundColor: "#ffffff",
                    zIndex: 10, 
                    borderBottom: "1px solid #dee2e6", 
                    padding: "0.75rem 1rem",
                    margin: 0,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                  }}>
                    <span style={{ fontWeight: "600", fontSize: "0.875rem", color: "#212529" }}>SELECT AGENTS FOR REPORT</span>
                  </div>
                  <div className="agent-report-dropdown-item" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setReportAgents([]);
                    // Close dropdown after selection
                    setTimeout(() => setShowReportPicker(false), 100);
                  }} style={{ padding: "0.5rem 1rem" }}>
                    <div className="agent-report-checkbox-wrapper">
                      <div className={`agent-report-checkbox ${reportAgents.length === 0 ? "checked" : ""}`}>
                        {reportAgents.length === 0 && <FontAwesomeIcon icon={faCheck} />}
                      </div>
                      <span style={{ whiteSpace: "nowrap", overflow: "visible" }}>All Agents</span>
                    </div>
                  </div>
                  <div className="agent-report-divider"></div>
                  {uniqueAgents.map((a) => {
                    const checked = reportAgents.includes(a);
                    return (
                      <div
                        key={a}
                        className="agent-report-dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setReportAgents((prev) =>
                            checked ? prev.filter((x) => x !== a) : [...prev, a]
                          );
                        }}
                        style={{ padding: "0.5rem 1rem" }}
                      >
                        <div className="agent-report-checkbox-wrapper">
                          <div className={`agent-report-checkbox ${checked ? "checked" : ""}`}>
                            {checked && <FontAwesomeIcon icon={faCheck} />}
                          </div>
                          <span style={{ whiteSpace: "nowrap", overflow: "visible", display: "block", width: "100%" }}>{a}</span>
                        </div>
                      </div>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown>
              <Button
                variant="dark"
                onClick={handleGenerateReport}
                disabled={filteredFeedback.length === 0}
                className="d-flex align-items-center justify-content-center"
                style={{ 
                  height: "38px",
                  padding: "0.375rem 0.75rem",
                  fontSize: "12px",
                  fontWeight: "normal",
                  borderRadius: "4px",
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-2" style={{ fontSize: "12px" }} />
                Generate Report
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {/* Search and Filter Section - Modern Advanced Design */}
          <div className="search-filter-section-modern" style={{ padding: "0.75rem 1rem" }}>
            <div className="d-flex align-items-center mb-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="d-flex align-items-center"
                style={{ 
                  width: "auto", 
                  minWidth: "fit-content", 
                  padding: "0.25rem 0.5rem",
                  fontSize: "0.875rem"
                }}
              >
                <FontAwesomeIcon icon={faFilter} className="me-1" style={{ fontSize: "0.75rem" }} />
                Filters
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className="ms-1"
                  style={{ 
                    fontSize: "0.65rem", 
                    transition: "transform 0.2s ease",
                    transform: showFilters ? "rotate(180deg)" : "rotate(0deg)"
                  }}
                />
              </Button>
            </div>

            {showFilters && (
            <>
            {/* First Row: Search + 4 Dropdowns */}
            <div className="d-flex gap-2 mb-2" style={{ flexWrap: "nowrap" }}>
              {/* Modern Search Input */}
              <motion.div
                style={{ flex: "2", minWidth: "200px" }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <label className="filter-label-modern">
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  Search Feedback
                </label>
                <div className="modern-search-wrapper">
                  <div className="search-icon-modern">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <input
                    type="text"
                    className="modern-search-input"
                    placeholder="Search by comment, customer, or ticket..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="clear-search-btn"
                      onClick={() => setSearchTerm("")}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </motion.div>

              {/* Modern Sentiment Dropdown */}
              <motion.div
                style={{ flex: "1", minWidth: "130px" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.15 }}
              >
                <label className="filter-label-modern">
                  <FontAwesomeIcon icon={faSmile} className="me-2" />
                  Sentiment
                </label>
                <Dropdown
                  show={showDropdowns.sentiment}
                  onToggle={(isOpen) =>
                    setShowDropdowns({ ...showDropdowns, sentiment: isOpen })
                  }
                >
                  <Dropdown.Toggle className="modern-dropdown-toggle" as="div">
                    <div className="modern-dropdown-button">
                      <span className={filterSentiment.length > 0 ? "has-selection" : ""}>
                        {getFilterDisplayText("sentiment")}
                      </span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`dropdown-arrow ${showDropdowns.sentiment ? "open" : ""}`}
                      />
                      {filterSentiment.length > 0 && (
                        <span className="filter-badge-modern">{filterSentiment.length}</span>
                      )}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="modern-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                    <div className="modern-dropdown-header">
                      <span>Select Sentiments</span>
                    </div>
                    <div className="modern-dropdown-item" onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFilterSentiment([]);
                    }}>
                      <div className="modern-checkbox-wrapper">
                        <div className={`modern-checkbox ${filterSentiment.length === 0 ? "checked" : ""}`}>
                          {filterSentiment.length === 0 && <FontAwesomeIcon icon={faCheck} />}
                        </div>
                        <span>All Sentiments</span>
                      </div>
                    </div>
                    <div className="modern-dropdown-divider"></div>
                    {["satisfied", "dissatisfied", "neutral"].map((sentiment) => {
                      const isChecked = filterSentiment.includes(sentiment);
                      const sentimentColors = {
                        satisfied: { bg: "#dcfce7", border: "#22c55e", icon: "#16a34a" },
                        dissatisfied: { bg: "#fee2e2", border: "#ef4444", icon: "#dc2626" },
                        neutral: { bg: "#fef3c7", border: "#f59e0b", icon: "#d97706" }
                      };
                      const colors = sentimentColors[sentiment] || sentimentColors.neutral;
                      return (
                        <div
                          key={sentiment}
                          className="modern-dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFilterOption("sentiment", sentiment);
                          }}
                        >
                          <div className="modern-checkbox-wrapper">
                            <div 
                              className={`modern-checkbox ${isChecked ? "checked" : ""}`}
                              style={isChecked ? { 
                                backgroundColor: colors.bg, 
                                borderColor: colors.border 
                              } : {}}
                            >
                              {isChecked && <FontAwesomeIcon icon={faCheck} style={{ color: colors.icon }} />}
                            </div>
                            <span>{sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </motion.div>

              {/* Modern Rating Dropdown */}
              <motion.div
                style={{ flex: "1", minWidth: "130px" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <label className="filter-label-modern">
                  <FontAwesomeIcon icon={faStar} className="me-2" />
                  Rating
                </label>
                <Dropdown
                  show={showDropdowns.rating}
                  onToggle={(isOpen) =>
                    setShowDropdowns({ ...showDropdowns, rating: isOpen })
                  }
                >
                  <Dropdown.Toggle className="modern-dropdown-toggle" as="div">
                    <div className="modern-dropdown-button">
                      <span className={filterRating.length > 0 ? "has-selection" : ""}>
                        {getFilterDisplayText("rating")}
                      </span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`dropdown-arrow ${showDropdowns.rating ? "open" : ""}`}
                      />
                      {filterRating.length > 0 && (
                        <span className="filter-badge-modern">{filterRating.length}</span>
                      )}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="modern-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                    <div className="modern-dropdown-header">
                      <span>Select Ratings</span>
                    </div>
                    <div className="modern-dropdown-item" onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFilterRating([]);
                    }}>
                      <div className="modern-checkbox-wrapper">
                        <div className={`modern-checkbox ${filterRating.length === 0 ? "checked" : ""}`}>
                          {filterRating.length === 0 && <FontAwesomeIcon icon={faCheck} />}
                        </div>
                        <span>All Ratings</span>
                      </div>
                    </div>
                    <div className="modern-dropdown-divider"></div>
                    {["5", "4", "3", "2", "1"].map((rating) => {
                      const isChecked = filterRating.includes(rating);
                      return (
                        <div
                          key={rating}
                          className="modern-dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFilterOption("rating", rating);
                          }}
                        >
                          <div className="modern-checkbox-wrapper">
                            <div className={`modern-checkbox ${isChecked ? "checked" : ""}`}>
                              {isChecked && <FontAwesomeIcon icon={faCheck} />}
                            </div>
                            <span>
                              {rating} <FontAwesomeIcon icon={faStar} className="text-warning ms-1" style={{ fontSize: "0.75rem" }} />
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </motion.div>

              {/* Modern Agent Dropdown */}
              <motion.div
                style={{ flex: "1", minWidth: "130px" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.25 }}
              >
                <label className="filter-label-modern">
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Agent
                </label>
                <Dropdown
                  show={showDropdowns.agent}
                  onToggle={(isOpen) =>
                    setShowDropdowns({ ...showDropdowns, agent: isOpen })
                  }
                >
                  <Dropdown.Toggle className="modern-dropdown-toggle" as="div">
                    <div className="modern-dropdown-button">
                      <span className={filterAgent.length > 0 ? "has-selection" : ""}>
                        {getFilterDisplayText("agent")}
                      </span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`dropdown-arrow ${showDropdowns.agent ? "open" : ""}`}
                      />
                      {filterAgent.length > 0 && (
                        <span className="filter-badge-modern">{filterAgent.length}</span>
                      )}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="modern-dropdown-menu modern-dropdown-menu-scroll" onClick={(e) => e.stopPropagation()}>
                    <div className="modern-dropdown-header">
                      <span>Select Agents</span>
                    </div>
                    <div className="modern-dropdown-item" onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFilterAgent([]);
                    }}>
                      <div className="modern-checkbox-wrapper">
                        <div className={`modern-checkbox ${filterAgent.length === 0 ? "checked" : ""}`}>
                          {filterAgent.length === 0 && <FontAwesomeIcon icon={faCheck} />}
                        </div>
                        <span>All Agents</span>
                      </div>
                    </div>
                    <div className="modern-dropdown-divider"></div>
                    {uniqueAgents.map((agent) => {
                      const isChecked = filterAgent.includes(agent);
                      return (
                        <div
                          key={agent}
                          className="modern-dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFilterOption("agent", agent);
                          }}
                        >
                          <div className="modern-checkbox-wrapper">
                            <div className={`modern-checkbox ${isChecked ? "checked" : ""}`}>
                              {isChecked && <FontAwesomeIcon icon={faCheck} />}
                            </div>
                            <span>{agent}</span>
                          </div>
                        </div>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </motion.div>

              {/* Modern Category Dropdown */}
              <motion.div
                style={{ flex: "1", minWidth: "130px" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <label className="filter-label-modern">
                  <FontAwesomeIcon icon={faTag} className="me-2" />
                  Category
                </label>
                <Dropdown
                  show={showDropdowns.category}
                  onToggle={(isOpen) =>
                    setShowDropdowns({ ...showDropdowns, category: isOpen })
                  }
                >
                  <Dropdown.Toggle className="modern-dropdown-toggle" as="div">
                    <div className="modern-dropdown-button">
                      <span className={filterCategory.length > 0 ? "has-selection" : ""}>
                        {getFilterDisplayText("category")}
                      </span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`dropdown-arrow ${showDropdowns.category ? "open" : ""}`}
                      />
                      {filterCategory.length > 0 && (
                        <span className="filter-badge-modern">{filterCategory.length}</span>
                      )}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="modern-dropdown-menu modern-dropdown-menu-scroll" onClick={(e) => e.stopPropagation()}>
                    <div className="modern-dropdown-header">
                      <span>Select Categories</span>
                    </div>
                    <div className="modern-dropdown-item" onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFilterCategory([]);
                    }}>
                      <div className="modern-checkbox-wrapper">
                        <div className={`modern-checkbox ${filterCategory.length === 0 ? "checked" : ""}`}>
                          {filterCategory.length === 0 && <FontAwesomeIcon icon={faCheck} />}
                        </div>
                        <span>All Categories</span>
                      </div>
                    </div>
                    <div className="modern-dropdown-divider"></div>
                    {uniqueCategories.map((category) => {
                      const isChecked = filterCategory.includes(category);
                      return (
                        <div
                          key={category}
                          className="modern-dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFilterOption("category", category);
                          }}
                        >
                          <div className="modern-checkbox-wrapper">
                            <div className={`modern-checkbox ${isChecked ? "checked" : ""}`}>
                              {isChecked && <FontAwesomeIcon icon={faCheck} />}
                            </div>
                            <span>{category}</span>
                          </div>
                        </div>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </motion.div>
            </div>

            {/* Second Row: Date Range and Clear Filters */}
            <div className="d-flex gap-2 align-items-end" style={{ flexWrap: "nowrap" }}>
              <motion.div
                style={{ flex: "0 0 160px" }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.35 }}
              >
                <label className="filter-label-modern">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                  Start Date
                </label>
                <div className="modern-date-wrapper">
                  <div className="date-icon-modern">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <input
                    type="date"
                    className="modern-date-input"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, start: e.target.value })
                    }
                  />
                </div>
              </motion.div>
              <motion.div
                style={{ flex: "0 0 160px" }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <label className="filter-label-modern">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                  End Date
                </label>
                <div className="modern-date-wrapper">
                  <div className="date-icon-modern">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <input
                    type="date"
                    className="modern-date-input"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, end: e.target.value })
                    }
                  />
                </div>
              </motion.div>
              <motion.div
                style={{ flex: "0 0 auto" }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.45 }}
              >
                <button
                  className="modern-clear-filters-btn"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterSentiment([]);
                    setFilterRating([]);
                    setFilterAgent([]);
                    setFilterCategory([]);
                    setDateRange({ start: "", end: "" });
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Clear All Filters
                </button>
              </motion.div>
            </div>
            </>
            )}
          </div>

          {/* Summary Statistics */}
          <div className="summary-stats" style={{ width: "100%", padding: "0.75rem 1rem" }}>
            <div className="d-flex flex-wrap g-4" style={{ width: "100%", gap: "1rem" }}>
              <div style={{ flex: "1 1 0%", minWidth: "150px", maxWidth: "100%" }}>
                <div className="d-flex align-items-center summary-stat-item">
                  <div className="summary-stat-icon">
                    <FontAwesomeIcon icon={faComments} />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-dark">
                      {hasAnimated ? (
                        <AnimatedCounter value={summaryStats.totalFeedback} duration={1500} />
                      ) : (
                        summaryStats.totalFeedback
                      )}
                    </h6>
                    <small className="text-muted">Total Feedback</small>
                  </div>
                </div>
              </div>
              <div style={{ flex: "1 1 0%", minWidth: "150px", maxWidth: "100%" }}>
                <div className="d-flex align-items-center summary-stat-item">
                  <div className="summary-stat-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-dark">
                      {hasAnimated ? (
                        <>
                          <AnimatedCounter value={summaryStats.averageRating} decimals={1} duration={1500} />
                          /{summaryStats.averageScale}
                        </>
                      ) : (
                        `${summaryStats.averageRating}/${summaryStats.averageScale}`
                      )}
                    </h6>
                    <small className="text-muted">Average Rating</small>
                  </div>
                </div>
              </div>
              <div style={{ flex: "1 1 0%", minWidth: "150px", maxWidth: "100%" }}>
                <div className="d-flex align-items-center summary-stat-item">
                  <div className="summary-stat-icon">
                    <FontAwesomeIcon icon={faSmile} />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-dark">
                      {hasAnimated ? (
                        <AnimatedCounter value={summaryStats.sentimentDistribution.positive} duration={1500} />
                      ) : (
                        summaryStats.sentimentDistribution.positive
                      )}
                    </h6>
                    <small className="text-muted">Positive Feedback</small>
                  </div>
                </div>
              </div>
              <div style={{ flex: "1 1 0%", minWidth: "150px", maxWidth: "100%" }}>
                <div className="d-flex align-items-center summary-stat-item">
                  <div className="summary-stat-icon">
                    <FontAwesomeIcon icon={faClipboardList} />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-dark">
                      {hasAnimated ? (
                        <AnimatedCounter value={summaryStats.ticketsSolved} duration={1500} />
                      ) : (
                        summaryStats.ticketsSolved
                      )}
                    </h6>
                    <small className="text-muted">Closed Tickets</small>
                  </div>
                </div>
              </div>
              <div style={{ flex: "1 1 0%", minWidth: "150px", maxWidth: "100%" }}>
                <div className="d-flex align-items-center summary-stat-item">
                  <div className="summary-stat-icon">
                    <FontAwesomeIcon icon={faChartBar} />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold text-dark">
                      {hasAnimated ? (
                        <>
                          <AnimatedCounter value={summaryStats.csat || 0} decimals={2} duration={1500} />
                        </>
                      ) : (
                        (Number(summaryStats.csat) || 0).toFixed(2)
                      )}
                    </h6>
                    <small className="text-muted">CSAT Score</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="p-4">
              <SkeletonLoader count={itemsPerPage} />
            </div>
          ) : (
            <ListGroup variant="flush" className="feedback-list flex-grow-1" ref={parent}>
              {currentFeedback.length > 0 ? (
                currentFeedback.map((feedback, index) => {
                  const sentimentKeyRaw = feedback.sentiment || "unknown";
                  const sentimentKey = sentimentKeyRaw.toLowerCase();
                  const sentimentLabel =
                    sentimentKeyRaw.charAt(0).toUpperCase() + sentimentKeyRaw.slice(1);
                  const sentimentData = getSentimentIcon(sentimentKey);
                  return (
                    <motion.div
                      key={`${feedback.ticket_id}-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <ListGroup.Item
                        className={`border-0 p-4 feedback-item sentiment-${sentimentKey}`}
                        onClick={() => handleFeedbackClick(feedback)}
                      >
                        <div className="feedback-item-body">
                          <div className={`customer-avatar ${sentimentKey}`}>
                            {getCustomerInitial(feedback.customer)}
                          </div>
                          <div className="feedback-content">
                            <div className="feedback-top">
                              <div className="flex-grow-1">
                                <h6 className="mb-0">{feedback.customer}</h6>
                                <ul className="feedback-meta">
                                  <li>
                                    <FontAwesomeIcon icon={faTicketAlt} />
                                    {feedback.ticket}
                                  </li>
                                  <li>
                                    <FontAwesomeIcon icon={faClock} />
                                    {feedback.timestamp}
                                  </li>
                                  <li>
                                    <FontAwesomeIcon icon={faTag} />
                                    {feedback.category}
                                  </li>
                                </ul>
                              </div>
                              <div className="feedback-actions">
                                <Badge className={`sentiment-badge ${sentimentKey}`}>
                                  <FontAwesomeIcon icon={sentimentData.icon} />
                                  {sentimentLabel}
                                </Badge>
                                <span className="rating-chip">
                                  <FontAwesomeIcon icon={faStar} />
                                  {feedback.rating}
                                  <span className="subtle">/{feedback.maxScale}</span>
                                </span>
                              </div>
                            </div>

                            <ReadMoreText
                              text={feedback.comment || "No comment provided"}
                              maxLength={140}
                            />

                            {feedback.tags && feedback.tags.length > 0 && (
                              <div className="feedback-tags">
                                {feedback.tags.slice(0, 3).map((tag, tagIndex) => (
                                  <span className="tag-pill" key={`${feedback.ticket}-${tag}-${tagIndex}`}>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="feedback-footer">
                              <div className="feedback-agent">
                                <span>Assigned agent</span>
                                <span>{feedback.agent}</span>
                              </div>
                              <div className="feedback-footer-actions">
                                <button
                                  type="button"
                                  className="feedback-view-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFeedbackClick(feedback);
                                  }}
                                >
                                  View details
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    </motion.div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <FontAwesomeIcon icon={faComments} />
                  </div>
                  <h6 className="mb-2">No feedback found</h6>
                  <p className="text-muted mb-0">
                    {searchTerm ||
                    filterSentiment.length > 0 ||
                    filterRating.length > 0 ||
                    filterAgent.length > 0 ||
                    filterCategory.length > 0 ||
                    dateRange.start ||
                    dateRange.end
                      ? "Try adjusting your search or filter criteria."
                      : "No feedback available at the moment."}
                  </p>
                </div>
              )}
            </ListGroup>
          )}
        </Card.Body>
        {!isLoading && filteredFeedback.length > 0 && (
          <Card.Footer className="bg-light border-0 mt-2">
            <div className="pagination-container">
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="pagination-info">
                  Showing {startIndex + 1}-
                  {Math.min(endIndex, filteredFeedback.length)} of{" "}
                  {filteredFeedback.length} feedback entries
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center">
                    <span className="text-muted small me-2">Show:</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={itemsPerPage}
                      onChange={(e) =>
                        handleItemsPerPageChange(Number(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                    </select>
                    <span className="text-muted small ms-2">per page</span>
                  </div>
                  {totalPages > 1 && (
                    <div className="pagination-controls">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="pagination-button"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </Button>
                      <div className="d-flex align-items-center">
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, index) => {
                            let pageNumber;
                            if (totalPages <= 5) {
                              pageNumber = index + 1;
                            } else if (currentPage <= 3) {
                              pageNumber = index + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + index;
                            } else {
                              pageNumber = currentPage - 2 + index;
                            }

                            return (
                              <Button
                                key={pageNumber}
                                variant={
                                  currentPage === pageNumber
                                    ? "primary"
                                    : "outline-secondary"
                                }
                                size="sm"
                                onClick={() => handlePageChange(pageNumber)}
                                className="page-number-button me-1"
                              >
                                {pageNumber}
                              </Button>
                            );
                          }
                        )}
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="pagination-button"
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* Agent Report Preview Modal */}
      <Modal 
        show={showReportModal} 
        onHide={() => setShowReportModal(false)} 
        size="xl" 
        centered
        className="report-preview-modal"
      >
        <Modal.Header closeButton style={{ background: "#f0f4f8", borderBottom: "1px solid #dee2e6" }}>
          <Modal.Title style={{ color: "#2c3e50", fontWeight: "600", fontSize: "clamp(1rem, 2vw, 1.25rem)" }}>
            <FontAwesomeIcon icon={faFilePdf} className="me-2" style={{ color: "#5a6c7d" }} />
            Agent Survey Report Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto", padding: "1rem", overflowX: "visible" }}>
          {reportData && (() => {
            const getCSATColor = (csatValue) => {
              if (csatValue >= 90) return "#d4edda";
              if (csatValue < 80) return "#f8d7da";
              return null;
            };
            
            // Generate colors for categories dynamically
            const getCategoryColor = (index) => {
              const colors = ["#e3f2fd", "#fff3e0", "#f3e5f5", "#e8f5e9", "#fce4ec", "#e0f2f1", "#fff9c4", "#f1f8e9"];
              return colors[index % colors.length];
            };
            
            const categories = reportData.categories || [];
            
            return (
              <div className="report-preview-container">
                <div className="report-header mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="mb-0 fw-bold" style={{ color: "#495057" }}>Agent Survey Report</h4>
                    <small className="text-muted">Generated: {reportData.generatedAt}</small>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "#6c757d", fontSize: "0.875rem" }} />
                      <span className="fw-semibold" style={{ color: "#495057", fontSize: "0.9rem" }}>
                        Report Period: <span style={{ color: "#6c757d" }}>{reportData.dateRange}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="table-responsive report-table-wrapper">
                  <table className="table table-bordered report-table" style={{ fontSize: "0.875rem", minWidth: "1000px" }}>
                    <thead>
                      <tr style={{ background: "#f8f9fa" }}>
                        <th rowSpan="2" className="align-middle text-center fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>
                          Agent
                        </th>
                        {categories.map((cat, idx) => (
                          <th key={cat} colSpan="5" className="text-center fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem", background: getCategoryColor(idx) }}>
                            {cat}
                          </th>
                        ))}
                        <th colSpan="5" className="text-center fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem", background: "#f3e5f5" }}>
                          Overall
                        </th>
                        <th rowSpan="2" className="align-middle text-center fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>
                          Overall Ticket Load
                        </th>
                      </tr>
                      <tr style={{ background: "#f8f9fa" }}>
                        {categories.map((cat) => (
                          <React.Fragment key={cat}>
                            <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>Total Closures</th>
                            <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>Total Surveys</th>
                            <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>SRR</th>
                            <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>Positive</th>
                            <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>CSAT</th>
                          </React.Fragment>
                        ))}
                        <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>Total Closures</th>
                        <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>Total Surveys</th>
                        <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>SRR</th>
                        <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>Positive</th>
                        <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>CSAT</th>
                        <th className="text-center fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.5rem", fontSize: "0.8rem" }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.agentRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{row.name}</td>
                          {categories.map((cat) => {
                            const catData = row.categories[cat] || { closures: 0, surveys: 0, srr: 0, positive: 0, csat: 0 };
                            return (
                              <React.Fragment key={cat}>
                                <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catData.closures}</td>
                                <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catData.surveys}</td>
                                <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catData.srr.toFixed(0)}%</td>
                                <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catData.positive}</td>
                                <td 
                                  className="text-end fw-semibold" 
                                  style={{ 
                                    border: "1px solid #dee2e6", 
                                    padding: "0.75rem",
                                    backgroundColor: getCSATColor(catData.csat)
                                  }}
                                >
                                  {catData.csat.toFixed(1)}%
                                </td>
                              </React.Fragment>
                            );
                          })}
                          <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{row.overall.closures}</td>
                          <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{row.overall.surveys}</td>
                          <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{row.overall.srr.toFixed(0)}%</td>
                          <td className="text-end" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{row.overall.positive}</td>
                          <td 
                            className="text-end fw-semibold" 
                            style={{ 
                              border: "1px solid #dee2e6", 
                              padding: "0.75rem",
                              backgroundColor: getCSATColor(row.overall.csat)
                            }}
                          >
                            {row.overall.csat.toFixed(1)}%
                          </td>
                          <td className="text-end fw-semibold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{row.load.toFixed(0)}%</td>
                        </tr>
                      ))}
                      <tr style={{ background: "#f8f9fa", fontWeight: "700" }}>
                        <td className="fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>Total</td>
                        {categories.map((cat) => {
                          const catTotal = reportData.totalsRow[cat] || { closures: 0, surveys: 0, srr: 0, positive: 0, csat: 0 };
                          return (
                            <React.Fragment key={cat}>
                              <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catTotal.closures}</td>
                              <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catTotal.surveys}</td>
                              <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catTotal.srr.toFixed(0)}%</td>
                              <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{catTotal.positive}</td>
                              <td 
                                className="text-end fw-bold" 
                                style={{ 
                                  border: "1px solid #dee2e6", 
                                  padding: "0.75rem",
                                  backgroundColor: getCSATColor(catTotal.csat)
                                }}
                              >
                                {catTotal.csat.toFixed(1)}%
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{reportData.totalsRow.overall.closures}</td>
                        <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{reportData.totalsRow.overall.surveys}</td>
                        <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{reportData.totalsRow.overall.srr.toFixed(0)}%</td>
                        <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>{reportData.totalsRow.overall.positive}</td>
                        <td 
                          className="text-end fw-bold" 
                          style={{ 
                            border: "1px solid #dee2e6", 
                            padding: "0.75rem",
                            backgroundColor: getCSATColor(reportData.totalsRow.overall.csat)
                          }}
                        >
                          {reportData.totalsRow.overall.csat.toFixed(1)}%
                        </td>
                        <td className="text-end fw-bold" style={{ border: "1px solid #dee2e6", padding: "0.75rem" }}>100%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer style={{ background: "#f8f9fa", borderTop: "1px solid #dee2e6" }}>
          <Button 
            variant="secondary" 
            onClick={() => setShowReportModal(false)}
          >
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={exportAgentMatrixReportPdf}
            style={{ background: "#495057", border: "none" }}
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Download PDF
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Detailed Feedback Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Survey Feedback Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFeedback && (
            <div className="p-3">
              <div className="row">
                <div className="col-md-8">
                  <h5 className="mb-3 fw-bold">{selectedFeedback.customer}</h5>

                  <div className="mb-4">
                    <h6 className="text-muted mb-2">Customer Feedback</h6>
                    <div className="feedback-details">
                      <p className="mb-0">{selectedFeedback.comment}</p>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Customer Information</h6>
                      <div className="small">
                        <div className="mb-1">
                          <strong>Email:</strong>{" "}
                          {selectedFeedback.customerEmail}
                        </div>
                        <div className="mb-1">
                          <strong>Phone:</strong>{" "}
                          {selectedFeedback.customerPhone || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Ticket Information</h6>
                      <div className="small">
                        <div className="mb-1">
                          <strong>Ticket ID:</strong> {selectedFeedback.ticket}
                        </div>
                        <div className="mb-1">
                          <strong>Category:</strong> {selectedFeedback.category}
                        </div>
                        <div className="mb-1">
                          <strong>Priority:</strong>
                          <Badge
                            bg={
                              selectedFeedback.priority === "High"
                                ? "danger"
                                : selectedFeedback.priority === "Medium"
                                ? "warning"
                                : "success"
                            }
                            className="ms-1"
                          >
                            {selectedFeedback.priority}
                          </Badge>
                        </div>
                        <div className="mb-1">
                          <strong>Resolution Time:</strong>{" "}
                          {selectedFeedback.resolutionTime}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="feedback-info-card">
                    <h6 className="text-muted mb-3">Agent Information</h6>
                    <div className="mb-3">
                      <div className="fw-bold">{selectedFeedback.agent}</div>
                      <div className="text-muted small">
                        {selectedFeedback.agentEmail}
                      </div>
                    </div>

                    <h6 className="text-muted mb-2">Overall Rating</h6>
                    <div className="mb-3 rating-stars">
                      {getRatingStars(selectedFeedback.rating)}
                      <span className="ms-2 fw-bold">
                        {selectedFeedback.rating}/
                        {selectedFeedback.maxScale || 5}
                      </span>
                    </div>

                    <h6 className="text-muted mb-2">Sentiment</h6>
                    <div className="mb-3">
                      {(() => {
                        const sentimentKeyRaw = selectedFeedback.sentiment || "unknown";
                        const sentimentKey = sentimentKeyRaw.toLowerCase();
                        const sentimentLabel =
                          sentimentKeyRaw.charAt(0).toUpperCase() + sentimentKeyRaw.slice(1);
                        const sentimentData = getSentimentIcon(sentimentKey);
                        return (
                          <Badge className={`sentiment-badge ${sentimentKey}`}>
                            <FontAwesomeIcon
                              icon={sentimentData.icon}
                              className="me-1"
                            />
                            {sentimentLabel}
                          </Badge>
                        );
                      })()}
                    </div>

                    <h6 className="text-muted mb-2">Tags</h6>
                    <div className="mb-3">
                      {selectedFeedback.tags.map((tag, index) => (
                        <Badge key={index} bg="secondary" className="tag-badge">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    {/* Survey Details Toggle Button - integrated in agent info */}
                    {selectedFeedback.surveyTemplate && (
                      <div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            setShowSurveyDetails(!showSurveyDetails)
                          }
                          className="survey-toggle-btn w-100"
                        >
                          <FontAwesomeIcon
                            icon={showSurveyDetails ? faEyeSlash : faEye}
                             className="me-2"
                          />
                          {showSurveyDetails ? "Hide Survey" : "View Survey"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Survey Details Section */}
              {showSurveyDetails && selectedFeedback.surveyTemplate && (
                <div className="survey-details-section">
                  <div className="survey-header">
                    <div className="survey-title">
                      <FontAwesomeIcon
                        icon={faClipboardList}
                        className="me-2 text-primary"
                      />
                      <h4 className="mb-0 fw-bold text-primary">
                        {selectedFeedback.surveyTemplate.title}
                      </h4>
                    </div>
                    <div className="survey-stats">
                      <Badge bg="info" className="me-2">
                        {selectedFeedback.surveyTemplate.questions.length}{" "}
                        Questions
                      </Badge>
                      <Badge bg="success">Completed</Badge>
                    </div>
                  </div>

                  <div className="survey-questions-container">
                    {selectedFeedback.surveyTemplate.questions.map(
                      (question, index) => (
                        <div key={question.id} className="survey-question-card">
                          <div className="question-number">
                            <span className="question-badge">{index + 1}</span>
                          </div>
                          <div className="question-content">
                            <div className="question-header">
                              <h6 className="question-text">
                                {question.question}
                                {question.required && (
                                  <span className="required-indicator">*</span>
                                )}
                                {question.isScoreQuestion && (
                                  <Badge
                                    bg="primary"
                                    className="score-question-badge ms-2"
                                  >
                                    <FontAwesomeIcon
                                      icon={faChartBar}
                                      className="me-1"
                                    />
                                    Score Question
                                  </Badge>
                                )}
                              </h6>

                              <div className="question-meta">
                                <Badge
                                  bg="light"
                                  text="dark"
                                  className="question-type-badge"
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      question.type === "stars"
                                        ? faStar
                                        : question.type === "smileys"
                                        ? faSmile
                                        : question.type === "scale"
                                        ? faChartBar
                                        : faEdit
                                    }
                                    className="me-1"
                                  />
                                  {question.type.charAt(0).toUpperCase() +
                                    question.type.slice(1)}
                                  {question.type !== "text" &&
                                    ` (${question.maxRating} max)`}
                                </Badge>
                                <Badge
                                  bg="primary"
                                  className="score-question-badge ms-2"
                                >
                                  <FontAwesomeIcon
                                    icon={faChartBar}
                                    className="me-1"
                                  />
                                  Score Question
                                </Badge>
                              </div>
                            </div>
                            <div className="answer-display">
                              {renderSurveyAnswer(question)}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default XSurveyFeed;
